import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { remboursementsApi, type Remboursement } from '../services/api';
import { useToast } from '../context/ToastContext';
import { formatApiError } from '../lib/api-error';
import { Spinner } from '../components/shared/Spinner';
import { useValideursPaiements } from '../hooks/useValideursPaiements';
import {
  ChoixValideur,
  EtatListeValideurs,
  repartitionParValideur,
  valideurCommun,
} from '../components/paiements/ChoixValideur';
import { ReponseMotifRefus } from '../components/paiements/ReponseMotifRefus';
import { confirmationSansReponse, lignesSansReponse } from '../lib/reponse-attendue';
import { nomDuValideur, valideurEffectif } from '../../server/lib/valideurs';
import { BANQUES_VIREMENT } from '../../server/lib/banques-virement';

/**
 * Module Paiements — écran « Remboursements d'avoirs » (Valérie / admin).
 * Avoirs clients ouverts (Zoho Credit Notes) → choix banque/date → soumission
 * au valideur choisi (même workflow que les paiements fournisseurs). Une
 * seule section à l'écran : son sélecteur vaut pour toutes les lignes.
 */

function eur(n: number): string {
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

export function PaiementsRemboursementsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [choixValideur, setChoixValideur] = useState<Record<string, string>>({});
  // Requête retenue dans une variable, et pas seulement sa donnée : son échec
  // doit pouvoir s'afficher, sans quoi la colonne « Valideur » se vide en
  // silence.
  const valideursQ = useValideursPaiements();
  const valideurs = valideursQ.data?.valideurs ?? [];
  const plusieursValideurs = valideurs.length > 1;

  const q = useQuery({
    queryKey: ['remboursements', 'a-rembourser'],
    queryFn: () => remboursementsApi.list(),
    staleTime: 30_000,
  });

  const syncMutation = useMutation({
    mutationFn: () => remboursementsApi.sync(),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['remboursements'] });
      toast.success(`Synchro Zoho : ${r.created} ajouté(s), ${r.updated} mis à jour (${r.total_open} avoir(s) ouverts).`);
    },
    onError: (err) => toast.error(`Synchro échouée : ${formatApiError(err)}`),
  });

  const updateMutation = useMutation({
    mutationFn: (v: {
      id: string;
      banque?: string | null;
      date_remboursement?: string | null;
      reponse_admin?: string | null;
    }) =>
      remboursementsApi.update(v.id, {
        banque: v.banque,
        date_remboursement: v.date_remboursement,
        reponse_admin: v.reponse_admin,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['remboursements', 'a-rembourser'] }),
    onError: (err) => toast.error(`Modification échouée : ${formatApiError(err)}`),
  });

  const submitMutation = useMutation({
    mutationFn: (ids: string[]) =>
      remboursementsApi.submit(ids.map((id) => ({ id, valideur_email: valideurDe(id) || undefined }))),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['remboursements'] });
      setSelected(new Set());
      setChoixValideur({});
      let msg = `${r.submitted} remboursement(s) soumis : ${repartitionParValideur(r.par_valideur ?? [])}.`;
      if (!r.email_sent) msg += ' (email non envoyé : SMTP non configuré)';
      if (r.blocked.length) msg += ` ${r.blocked.length} bloqué(s).`;
      toast.success(msg);
    },
    // `formatApiError` et non `String(err)` : la soumission renvoie depuis le
    // 14/10/2026 un refus détaillé (« Valideur inconnu », avec le champ fautif),
    // que `String(err)` réduisait à « Error: … » — illisible.
    onError: (err) => toast.error(`Soumission échouée : ${formatApiError(err)}`),
  });

  const rows = q.data?.remboursements ?? [];
  const missingCount = rows.filter((r) => r.iban_manquant).length;
  const selectableRows = rows.filter((r) => !r.iban_manquant && !!r.banque);
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selected.has(r.id));
  const selectedTotal = rows.filter((r) => selected.has(r.id)).reduce((s, r) => s + r.montant, 0);

  /** Le valideur qui recevra la ligne : choisi à l'écran, sinon le sien, sinon le défaut. */
  function valideurDe(id: string): string {
    if (valideurs.length === 0) return '';
    return choixValideur[id] ?? valideurEffectif(rows.find((r) => r.id === id)?.valideur_email, valideurs);
  }
  function choisirValideur(ids: string[], email: string) {
    setChoixValideur((prev) => ({ ...prev, ...Object.fromEntries(ids.map((id) => [id, email])) }));
  }
  const compteParValideur = new Map<string, number>();
  for (const id of selected) compteParValideur.set(valideurDe(id), (compteParValideur.get(valideurDe(id)) ?? 0) + 1);
  const selectionParValideur = [...compteParValideur].map(([email, lignes]) => ({
    nom: nomDuValideur(email, valideurs),
    lignes,
  }));

  function toggle(r: Remboursement) {
    if (r.iban_manquant || !r.banque) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(r.id)) next.delete(r.id);
      else next.add(r.id);
      return next;
    });
  }
  function toggleAll() {
    setSelected((prev) => {
      if (allSelected) return new Set();
      return new Set(selectableRows.map((r) => r.id));
    });
  }

  /**
   * Soumission : on alerte quand un avoir refusé repart SANS réponse au motif.
   * Le motif est souvent une question — le renvoyer tel quel expose à un second
   * refus pour la même raison. La règle et le texte viennent du module pur
   * `src/lib/reponse-attendue.ts`, partagé avec l'écran des factures ; seuls les
   * mots de l'objet changent (un avoir porte un client et un numéro d'avoir).
   */
  function handleSubmit() {
    const ids = [...selected];
    const texte = confirmationSansReponse(lignesSansReponse(rows, selected), {
      objet: 'avoir',
      refuses: 'refusé(s)',
      tiers: (a) => a.customer_name,
      numero: (a) => a.credit_note_number,
    });
    if (texte && !window.confirm(texte)) return;
    submitMutation.mutate(ids);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Remboursements d'avoirs</h1>
          <p className="text-sm text-slate-500 mt-1">
            Avoirs clients ouverts (Zoho Books), à préparer puis soumettre au valideur choisi.
          </p>
        </div>
        <button
          type="button"
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
          className="px-3 py-1.5 rounded-md text-sm bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50 inline-flex items-center gap-2"
        >
          {syncMutation.isPending && <Spinner />}
          Synchroniser depuis Zoho
        </button>
      </div>

      {missingCount > 0 && (
        <div className="rounded-md bg-amber-50 text-amber-800 px-4 py-2.5 text-sm">
          ⚠ {missingCount} avoir(s) sans coordonnées bancaires :{' '}
          <Link to="/paiements/beneficiaires" className="underline">
            ajouter un bénéficiaire client
          </Link>{' '}
          pour pouvoir rembourser.
        </div>
      )}

      <EtatListeValideurs
        erreur={valideursQ.isError ? valideursQ.error : null}
        chargement={valideursQ.isLoading}
        relanceEnCours={valideursQ.isFetching}
        onReessayer={() => valideursQ.refetch()}
        objet="avoir"
      />

      {q.isLoading ? (
        <p className="text-slate-500">Chargement…</p>
      ) : q.isError ? (
        <p className="text-[#C0423F]">Erreur : {formatApiError(q.error)}</p>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-500">
          Aucun avoir à rembourser. Lance une synchro Zoho.
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 font-medium flex items-center justify-between gap-3">
              <span>À rembourser ({rows.length})</span>
              {plusieursValideurs && (
                <label className="flex items-center gap-2 text-sm font-normal text-slate-700">
                  Valideur de tous les avoirs :
                  <ChoixValideur
                    valideurs={valideurs}
                    value={valideurCommun(rows.map((r) => valideurDe(r.id)))}
                    onChange={(email) => choisirValideur(rows.map((r) => r.id), email)}
                    label="Valideur de tous les avoirs"
                  />
                </label>
              )}
            </div>
            <table className="w-full text-sm">
              <thead className="text-slate-500 text-left text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 w-8">
                    <input type="checkbox" checked={allSelected} disabled={selectableRows.length === 0} onChange={toggleAll} />
                  </th>
                  <th className="px-3 py-2 font-medium">Client</th>
                  <th className="px-3 py-2 font-medium">N° Avoir</th>
                  <th className="px-3 py-2 font-medium text-right">Montant</th>
                  <th className="px-3 py-2 font-medium">Date avoir</th>
                  <th className="px-3 py-2 font-medium">Date remboursement</th>
                  <th className="px-3 py-2 font-medium">Banque</th>
                  <th className="px-3 py-2 font-medium">Valideur</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id} className={r.iban_manquant ? 'bg-amber-50/40' : ''}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(r.id)}
                        disabled={r.iban_manquant || !r.banque}
                        onChange={() => toggle(r)}
                      />
                    </td>
                    <td className="px-3 py-2 font-medium">
                      {r.customer_name}
                      {r.iban_manquant && (
                        <span className="ml-2 text-xs text-amber-600" title="Bénéficiaire client / IBAN introuvable">⚠ IBAN manquant</span>
                      )}
                      {/* Le motif du refus, et la réponse à y apporter. Sans
                          lui, la question du valideur n'arrivait que par
                          courriel : l'avoir repartait tel quel et se faisait
                          refuser une seconde fois pour la même raison. */}
                      {r.rejet_motif && (
                        <div className="mt-1 rounded bg-red-50 ring-1 ring-inset ring-red-100 px-2 py-1.5">
                          <div className="text-xs text-red-700">⨯ Refusé : {r.rejet_motif}</div>
                          <ReponseMotifRefus
                            reponse={r.reponse_admin}
                            reponseAt={r.reponse_at}
                            valideur={nomDuValideur(valideurDe(r.id), valideurs) || 'valideur'}
                            onSave={(reponse) => updateMutation.mutate({ id: r.id, reponse_admin: reponse })}
                          />
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{r.credit_note_number}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{eur(r.montant)}</td>
                    <td className="px-3 py-2 text-slate-500">{r.date_avoir ?? '-'}</td>
                    <td className="px-3 py-2">
                      <input
                        type="date"
                        value={r.date_remboursement ?? ''}
                        onChange={(e) => updateMutation.mutate({ id: r.id, date_remboursement: e.target.value || null })}
                        className="rounded border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={r.banque ?? ''}
                        onChange={(e) => updateMutation.mutate({ id: r.id, banque: e.target.value || null })}
                        className={`rounded border px-2 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 ${r.banque ? 'border-slate-300' : 'border-amber-400'}`}
                      >
                        <option value="">Choisir</option>
                        {BANQUES_VIREMENT.map((b) => (
                          <option key={b} value={b}>{b}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      {plusieursValideurs ? (
                        <ChoixValideur
                          valideurs={valideurs}
                          value={valideurDe(r.id)}
                          onChange={(email) => choisirValideur([r.id], email)}
                          label={`Valideur de l'avoir ${r.credit_note_number}`}
                        />
                      ) : (
                        <span className="text-xs text-[#384872]">{nomDuValideur(valideurDe(r.id), valideurs)}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="sticky bottom-0 bg-white border border-slate-200 rounded-lg px-4 py-3 flex items-center justify-between shadow-sm">
            <span className="text-sm text-slate-600">
              {selected.size} sélectionné(s) · {eur(selectedTotal)}
            </span>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={selected.size === 0 || submitMutation.isPending}
              className="px-4 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {submitMutation.isPending && <Spinner />}
              Soumettre à validation
              {selected.size > 0 && valideurs.length > 0 && ` (${repartitionParValideur(selectionParValideur)})`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
